import { BaseClient, type Paged } from '../base-client';
import type {
  CreateCustomerExpenseInput,
  CustomerExpense,
  CustomerExpenseListQuery,
  PatchCustomerExpenseInput,
} from './types';

type One = { data: CustomerExpense };

const BY_ID_BASE = '/api/customer-expenses';

function listQuery(opts: CustomerExpenseListQuery): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (opts.asOf) query.asOf = opts.asOf;
  if (opts.limit != null) query.limit = opts.limit;
  if (opts.offset != null) query.offset = opts.offset;
  return query;
}

const collection = (customerId: string) =>
  `/api/customers/${encodeURIComponent(customerId)}/expenses`;

export class CustomerExpensesClient extends BaseClient {
  /**
   * A customer's pass-through lines. Pass `asOf` for the ones in force on that
   * date — there may be several, unlike budgets, since a customer can carry any
   * number of items at once. Omit it for the full history, newest first.
   */
  list(customerId: string, opts: CustomerExpenseListQuery = {}): Promise<Paged<CustomerExpense>> {
    return this.request(collection(customerId), { query: listQuery(opts) });
  }

  get(id: string): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`);
  }

  /**
   * Add one line. Omit `description` / `cost` to take the catalog item's
   * current values, which are copied onto the row — a later catalog price
   * change will not rewrite this line.
   */
  create(customerId: string, input: CreateCustomerExpenseInput): Promise<One> {
    return this.request(collection(customerId), { method: 'POST', body: input });
  }

  patch(id: string, input: PatchCustomerExpenseInput): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /**
   * Hard delete. Use this only to remove a line that should never have existed
   * — STOPPING a charge is a `patch` setting `effectiveTo`, which keeps the line in
   * history so invoices generated inside the closed window still resolve
   * against what was actually billed.
   */
  delete(id: string): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
